import React from 'react';
import { get, find } from 'lodash';
import PropTypes from 'prop-types';

import ProductImage from 'components/consumer/cart/components/ProductImage';
import { isOptionMaterial } from 'commons/constants';
import Radio from 'components/essentials/Radio';
import ToggleSwitch from 'components/consumer/ToggleSwitch';
import IconCurbSide from 'components/icons/iconCurbSide';
import IconInHomeDelivery from 'components/icons/iconInHomeDelivery';
import useRandomId from 'hooks/useRandomId';
import DayPickerWrapper from './DayPickerWrapper';
import {
  getDelayedDeliveryTime,
  getDelayedDeliveryStartDate,
} from '../../../../withCheckoutUtils';

const descriptions = {
  curbside_delivery:
    'Your order will be delivered right to your doorstep or closest entrance but will not be unpacked or assembled. We recommend having an extra set of hands around to help.',
  inhome_delivery:
    'Delivery team will remove all packaging, bring item(s) indoor, and perform light assembly when required.',
};

const prop65WarningTypes = [
  'table lamp',
  'floor lamp',
  'chandelier',
  'pendant',
];
const ShippingGroup = ({
  deliverySelection,
  group,
  groupNumber,
  productionSelection,
  productionSelectionAdd,
  updateDeliveryInfo,
  updateDelayedShippingDates,
  cartData,
  shipping,
}) => {
  const deliveryMethodTitleId = useRandomId('ShippingGroup__delivery-method');

  const standardDeliveryTime = get(
    group.delivery_dates,
    'etaDelivery.short_format',
    ''
  );

  const isDelayed = productionSelection[group.id] === 'delayed_shipping';

  const delayedDeliveryStartDate = getDelayedDeliveryStartDate(
    get(group.delivery_dates, 'etaDelivery.end', null)
  );

  const delayedDeliveryTime =
    isDelayed && productionSelectionAdd[group.id]
      ? getDelayedDeliveryTime(productionSelectionAdd[group.id])
      : 'Pick a date';

  let areAllPickLater = false;
  if (group.cart && group.cart.length) {
    const pickLaterCount = group.cart.reduce(
      (acc, product) => (product.pickLater ? acc + 1 : acc),
      0
    );
    areAllPickLater = group.cart.length === pickLaterCount;
  }
  const isInHomeDelivery = deliverySelection?.[group.id].includes(
    'inhome_delivery'
  );
  const showDelayedShippingSwitch = areAllPickLater
    ? false
    : !!group.production_options.find(opt => opt.value === 'delayed_shipping');

  const shipmentTitle = `Shipment ${groupNumber}`;
  return (
    <div className="shipping-group" key={group.id}>
      <div className="border border-solid border-gray-light6">
        <div className="items-center flex justify-between bg-gray-light7 p-4 min-h-[75px]">
          <h2 className="text-xl font-bold text-gray m-0">{shipmentTitle}</h2>

          <div className="shipping-group__title-icon">
            {group.delivery_options[0].value?.includes('inhome_delivery') ? (
              <IconInHomeDelivery />
            ) : (
              <IconCurbSide />
            )}
          </div>
        </div>

        <div className="m-4">
          <div className="shipping-group__row flex flex-col px-0 py-[1.2rem] [border-top:1px_solid_#e5e5e5] min-h-[100px] lg:flex-row border-top:none">
            <h3 className="m-0 min-w-[9rem] max-w-[9rem] text-base font-bold">
              Estimated delivery
            </h3>

            <div className="text-base font-bold max-[1023px]:mt-2">
              {areAllPickLater ? (
                <span className="text-sm">
                  {`Arrival time can't be estimated until final fabric selection
                  is made`}
                </span>
              ) : (
                <span className="text-sm">
                  {isDelayed ? delayedDeliveryTime : standardDeliveryTime}
                </span>
              )}
              <div className="text-xs mt-4">
                {showDelayedShippingSwitch ? (
                  <ToggleSwitch
                    isChecked={
                      productionSelection[group.id] === 'delayed_shipping'
                    }
                    onClick={() => {
                      updateDeliveryInfo({
                        productionSelection: {
                          [group.id]:
                            productionSelection[group.id] !== 'delayed_shipping'
                              ? 'delayed_shipping'
                              : 'standard',
                        },
                      });
                    }}
                  >
                    Delayed: I&apos;d like to pick my own delivery date
                  </ToggleSwitch>
                ) : null}
                <div className="-ml-4 mb-[1.5625rem] w-full max-md:!mx-auto [&_.DayPicker]:w-full mt-1">
                  {isDelayed && (
                    <DayPickerWrapper
                      initialFromValue={
                        isDelayed
                          ? productionSelectionAdd[group.id]
                          : group.delivery_dates.etaShipping.start_date
                      }
                      startDate={delayedDeliveryStartDate}
                      onDateChange={delayedDelivery => {
                        updateDelayedShippingDates(group.id, delayedDelivery);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <fieldset
            className="shipping-group__row-fieldset border-0 m-0 p-0"
            aria-labelledby={deliveryMethodTitleId}
          >
            <div className="shipping-group__row flex flex-col px-0 py-[1.2rem] [border-top:1px_solid_#e5e5e5] min-h-[100px] lg:flex-row">
              <h3
                className="m-0 min-w-[9rem] max-w-[9rem] text-base font-bold"
                id={deliveryMethodTitleId}
              >
                Delivery method
              </h3>

              <div className="text-base font-bold max-[1023px]:mt-2">
                <div>
                  {group.delivery_options.length > 1
                    ? group.delivery_options.map(({ label, value, charge }) => {
                        const isChecked = value === deliverySelection[group.id];
                        return (
                          <Radio
                            value={value}
                            checked={isChecked}
                            onChange={evt => {
                              updateDeliveryInfo({
                                deliverySelection: {
                                  [group.id]: evt.target.value,
                                },
                              });
                            }}
                            key={value}
                          >
                            {`${label.split(' ')[0]} ${
                              charge ? ` - $${charge}` : ''
                            }`}
                          </Radio>
                        );
                      })
                    : group.delivery_options.length === 1 && (
                        <div className="text-base font-bold text-gray">
                          {group.delivery_options[0].label.split(' ')[0]} &nbsp;
                          {group.delivery_options[0].charge
                            ? `($${group.delivery_options[0].charge})`
                            : ''}
                        </div>
                      )}
                </div>
                <div className="text-sm">
                  {isInHomeDelivery
                    ? descriptions.inhome_delivery
                    : descriptions[group.delivery_options[0].value] || ''}
                  {!!group.accessory_options &&
                    group.accessory_options.charge > 0 && (
                      <div className="[&_span]:text-gray text-base text-gray mt.2.5">
                        <span className="cart-text-strong">
                          {group.accessory_options.label}{' '}
                        </span>
                        (+$
                        {group.accessory_options.charge})
                      </div>
                    )}
                </div>
              </div>
            </div>
          </fieldset>

          <div className="shipping-group__row flex flex-col px-0 py-[1.2rem] [border-top:1px_solid_#e5e5e5] min-h-[100px] lg:flex-row">
            <h3 className="m-0 min-w-[9rem] max-w-[9rem] text-base font-bold">
              Items in delivery
            </h3>

            <div className="text-base font-bold max-[1023px]:mt-2">
              <div className="flex flex-wrap mt-0 w-full">
                {group.cart &&
                  group.cart.length > 0 &&
                  group.cart.map(product => {
                    const cartProduct = find(cartData, { id: product.id });

                    return (
                      <div
                        className="flex flex-row flex-shrink mb-5 pr-6 w-full gap-2"
                        key={product.id}
                      >
                        <ProductImage
                          containerClass="bg-[hsla(0,0%,39.2%,0.04)] min-w-[100px] w-[100px]"
                          product={{
                            ...product,
                            images: cartProduct?.images,
                          }}
                          width={200}
                        />

                        <div className="cart-item-list__item-text">
                          <h3 className="text-sm font-bold leading-[1.75] text-gray m-0">
                            {product.name}
                          </h3>

                          <div className="text-sm leading-[1.75] text-gray font-normal">
                            {product.options &&
                              product.options
                                .filter(option => option.optionTypeId !== 19)
                                .map(o => (
                                  <div className="leading-[1.15rem]">
                                    {product.pickLater &&
                                    isOptionMaterial[o.optionTypeId]
                                      ? 'Material: Decide Later'
                                      : o.optionTypeId === 133
                                      ? o.name === 'Included'
                                        ? 'Extra Cushion'
                                        : null
                                      : o.optionTypeId === 135
                                      ? null
                                      : o.name}
                                  </div>
                                ))}
                          </div>

                          <div className="text-sm font-bold leading-[1.75] text-gray font-normal">
                            Qty: {product.quantity}
                          </div>

                          {shipping.state === 'CA' &&
                            prop65WarningTypes.includes(
                              product.product.type.name.toLowerCase()
                            ) && (
                              <div className="text-xs leading-[1.75] font-normal">
                                Prop 65 warning: This product can expose you to
                                chemicals including arsenic, which is known to
                                the State of California to cause cancer. For
                                more information, go to{' '}
                                <a
                                  className="text-brand no-underline focus:outline-0 focus-visible:underline hover:underline"
                                  href="https://www.P65Warnings.ca.gov"
                                  target="blank"
                                >
                                  www.P65Warnings.ca.gov
                                </a>
                                .
                              </div>
                            )}
                        </div>
                      </div>
                    );
                  })}
              </div>{' '}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ShippingGroup.propTypes = {
  cartData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      quantity: PropTypes.number,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          optionTypeId: PropTypes.number,
          name: PropTypes.string,
        })
      ),
    })
  ),
  deliverySelection: PropTypes.shape({
    [PropTypes.string]: PropTypes.string,
  }),
  group: PropTypes.shape({
    accessory_options: PropTypes.shape({
      charge: PropTypes.number,
      label: PropTypes.string,
    }),
    cart: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        quantity: PropTypes.number,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            optionTypeId: PropTypes.number,
            name: PropTypes.string,
          })
        ),
      })
    ),
    delivery_dates: PropTypes.shape({
      etaShipping: PropTypes.shape({
        start_date: PropTypes.string,
        end_date: PropTypes.string,
      }),
      delayedShipping: PropTypes.shape({
        start_date: PropTypes.string,
        end_date: PropTypes.string,
      }),
    }),
    id: PropTypes.number,
    name: PropTypes.string,
    delivery_options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
        charge: PropTypes.number,
      })
    ),
    production_options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
        charge: PropTypes.number,
      })
    ),
  }),
  groupNumber: PropTypes.number,
  productionSelection: PropTypes.shape({
    [PropTypes.string]: PropTypes.string,
  }),
  productionSelectionAdd: PropTypes.shape({
    [PropTypes.string]: PropTypes.string,
  }),
  updateDelayedShippingDates: PropTypes.func,
  updateDeliveryInfo: PropTypes.func,
  shipping: PropTypes.object,
};

export default ShippingGroup;
